<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
// NOTE: This component intentionally mutates the user prop as part of its design.
// The parent component passes a buffer object that is expected to be modified in-place.
// We kept this approach when we refactored from vue-class-component to Composition API.
// @TODO: rework the entire state management to a more modern approach.
import { computed, onMounted, ref, shallowRef, watch } from 'vue';
import { isEmpty } from 'lodash';
import { RestOperation, TradingPermissionOption } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { FormValidator } from '@/utils/helpers/input-form';
import i18n from '@/localisation/i18n';
import { formatTradingPermissionOption } from '@/modules/broker-admin/utils';
import { UserRequest } from '@/modules/user-accounts/models';
import {
  Company,
  CompanyListResponse,
  RawCompanyListResponse,
} from '@/modules/common/models/admin';
import VueI18n from 'vue-i18n';
import { ApiError } from '@/utils/errors/api-error';
import axios, { AxiosError } from 'axios';
import logger from '@/modules/common/services/logger.service';

const props = defineProps<{
  crudAction: RestOperation;
  user: UserRequest;
}>();

const emit = defineEmits<{
  (event: 'close-modal'): void;
}>();

const clientCompanyRoles = [
  { text: i18n.tc('trader-user'), value: 'trader-user' },
  { text: i18n.tc('trader-viewer'), value: 'trader-viewer' },
  { text: i18n.tc('trader-admin'), value: 'trader-admin' },
  { text: i18n.tc('ops-user'), value: 'ops-user' },
  { text: i18n.tc('config-admin'), value: 'config-admin' },
];

const brokerCompanyRoles = [
  { text: i18n.tc('broker-user'), value: 'broker-user' },
  { text: i18n.tc('broker-admin'), value: 'broker-admin' },
  { text: i18n.tc('broker-compliance-rep'), value: 'broker-compliance-rep' },
];

const cmpCompanies = shallowRef(null);
const cmpRoles = shallowRef(null);
const cmpTradingPermissions = shallowRef(null);

const title = computed(() => {
  return props.crudAction === RestOperation.Create ? 'adminCreateUser' : 'adminEditUser';
});

const originalCompany = shallowRef<Company | undefined | null>(null);
const companyKind = ref<'broker' | 'client'>('client');

const companies = shallowRef<Company[]>([]);
const tradingPermissionOptions = shallowRef<TradingPermissionOption[]>([]);
const allowedPermissions = shallowRef<TradingPermissionOption[]>([]);

const processing = ref(false);
const clErrors = ref<{ [index: string]: string }>({});
const svErrors = ref('');

const emailRules = [
  (v: string): true | VueI18n.TranslateResult =>
    /.+@.+/.test(v) || v === '' || i18n.t('formfield.InvalidEmail'),
];

const newUser = computed(() => {
  return !props.user || !props.user.emailAddress;
});

const companyOptions = computed(() => {
  if (!Array.isArray(companies.value)) {
    return [];
  }
  return companies.value.filter((c) => c.kind === companyKind.value);
});

const roles = computed(() => {
  return companyKind.value === 'broker' ? brokerCompanyRoles : clientCompanyRoles;
});

const multipleRolesSelectable = computed(() => {
  return (
    companyKind.value === 'broker' ||
    props.user.roles.length > 1 ||
    props.user.rolesPendingApproval.length > 1
  );
});

const company = computed(() => {
  if (!Array.isArray(companies.value)) {
    return undefined;
  }
  return companies.value.find((c) => c.id === props.user.companyID);
});

const effectiveTradingPermissions = computed(() => {
  if (!company.value) {
    return 0;
  }

  if (props.user.tradingPermissions === null) {
    return company.value.tradingPermissions;
  }

  return company.value.tradingPermissions & props.user.tradingPermissions;
});

watch(companyKind, () => {
  if (!company.value || company.value.kind !== companyKind.value) {
    props.user.companyID = '';
    props.user.roles = [];
    props.user.rolesPendingApproval = [];
  }
});

async function fetchBrokerCompanies(): Promise<void> {
  try {
    const { data } = await axios.get<RawCompanyListResponse>('/api/1/broker-admin/companies');
    companies.value = CompanyListResponse.fromData(data).companies;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function createBrokerAdminUser(user: UserRequest): Promise<void> {
  try {
    await axios.post(
      `/api/1/broker-admin/companies/${user.companyID}/users`,
      UserRequest.toData(user)
    );
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function updateBrokerAdminUser(user: UserRequest): Promise<void> {
  try {
    await axios.put(`/api/1/broker-admin/users/${user.id}`, UserRequest.toData(user));
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

// @TODO: we have this function duplicated in some components
// we should move to a service when we start fetching with connect-go
async function fetchTradingPermissionOptions(): Promise<void> {
  try {
    const { data } = await axios.get<{ options: TradingPermissionOption[] }>(
      '/api/1/trader/trading-permission-options'
    );
    tradingPermissionOptions.value = data.options;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

onMounted(async () => {
  try {
    await fetchBrokerCompanies();
    await fetchTradingPermissionOptions();
    // add `null` as option for permissions for 'inherit from company'
    allowedPermissions.value = tradingPermissionOptions.value.concat({ permission: null });
    // store the original company for the user when we opened the dialog,
    //  so we can warn if it's about to be changed
    originalCompany.value = company.value;

    if (company.value && company.value.kind === 'broker') {
      companyKind.value = 'broker';
    }
  } catch (e) {
    logger.warn(e);
  }
});

async function onSubmit(): Promise<void> {
  if (processing.value) {
    return;
  }
  processing.value = true;

  try {
    svErrors.value = '';
    clErrors.value = new FormValidator(props.user, [
      'companyID',
      'name',
      'emailAddress',
      'roles',
    ]).check();

    // if updating a user with pending role approvals, they should be submitted as roles or
    // they will be reset.
    if (
      props.user.accountStatus === 'pending-approval-for-roles' &&
      props.user.rolesPendingApproval !== null
    ) {
      props.user.roles = props.user.rolesPendingApproval;
    }

    // submit user
    if (isEmpty(clErrors.value)) {
      if (props.crudAction === RestOperation.Create) {
        await createBrokerAdminUser(props.user);
      } else {
        await updateBrokerAdminUser(props.user);
      }

      closeModalDialog();
    }
  } catch (e) {
    svErrors.value = errorString(e);
  } finally {
    processing.value = false;
  }
}

function formatCompanyName(c: Company): string {
  return `${c.name} (${c.displayId})`;
}

function formatTradingPermissionsLabel(item: TradingPermissionOption): string {
  return i18n.tc(formatTradingPermissionOption(item));
}

function closeModalDialog(): void {
  // allow parent to cleanup
  emit('close-modal');
}
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="800px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t(title) }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer v-if="svErrors">
            <VAlert dense type="error">{{ svErrors }}</VAlert>
          </VContainer>
          <VContainer>
            <VRow>
              <VCol>
                <VRadioGroup
                  v-model="companyKind"
                  dense
                  :disabled="!newUser"
                  hide-details
                  :readonly="!newUser"
                  row
                >
                  <VRadio color="form-field" label="Client" value="client"></VRadio>
                  <VRadio color="form-field" label="Broker" value="broker"></VRadio>
                </VRadioGroup>
              </VCol>
            </VRow>
            <VRow>
              <VCol>
                <VTextField
                  v-model="user.emailAddress"
                  autocomplete="email"
                  autofocus
                  :error-messages="clErrors.emailAddress"
                  :label="$t('loginEmail')"
                  required
                  :rules="emailRules"
                ></VTextField>
              </VCol>
            </VRow>
            <VRow v-if="crudAction === RestOperation.Create">
              <VCol>
                <VAlert border="left" dense type="info">
                  The user will receive an invitation by email to activate the account and set a
                  password. Login is blocked until the account has been activated.
                </VAlert>
              </VCol>
            </VRow>
            <VRow>
              <VCol>
                <VTextField
                  v-model="user.name"
                  autocomplete="username"
                  :error-messages="clErrors.name"
                  :label="$t('Full Name')"
                  placeholder="e.g. Susan Smith"
                  required
                ></VTextField>
              </VCol>
            </VRow>

            <VRow>
              <VCol>
                <VSelect
                  :ref="(r) => (cmpCompanies = r)"
                  v-model="user.companyID"
                  :error-messages="clErrors.companyID"
                  :item-text="formatCompanyName"
                  item-value="id"
                  :items="companyOptions"
                  :label="$t('Company')"
                ></VSelect>
              </VCol>
            </VRow>

            <VRow>
              <!-- If there are roles pending approval, we show the pending role not the actual role in the role
                    dropdown. This way the trader-admin can cancel the pending role assignment or switch it to a
                    different role assignment -->
              <VCol v-if="multipleRolesSelectable">
                <VSelect
                  v-if="
                    user.accountStatus === 'pending-approval-for-roles' &&
                    user.rolesPendingApproval !== null
                  "
                  :ref="(r) => (cmpRoles = r)"
                  v-model="user.rolesPendingApproval"
                  :error-messages="clErrors.roles"
                  :items="roles"
                  :label="$t('Role')"
                  multiple
                />
                <VSelect
                  v-else
                  :ref="(r) => (cmpRoles = r)"
                  v-model="user.roles"
                  :error-messages="clErrors.roles"
                  :items="roles"
                  :label="$t('Role')"
                  multiple
                />
              </VCol>
              <VCol v-else>
                <VSelect
                  v-if="
                    user.accountStatus === 'pending-approval-for-roles' &&
                    user.rolesPendingApproval !== null
                  "
                  :ref="(r) => (cmpRoles = r)"
                  v-model="user.rolesPendingApproval[0]"
                  :error-messages="clErrors.roles"
                  :items="roles"
                  :label="$t('Role')"
                />
                <VSelect
                  v-else
                  :ref="(r) => (cmpRoles = r)"
                  v-model="user.roles[0]"
                  :error-messages="clErrors.roles"
                  :items="roles"
                  :label="$t('Role')"
                />
              </VCol>
            </VRow>

            <VRow v-if="companyKind !== 'broker'">
              <VCol>
                <VSelect
                  :ref="(r) => (cmpTradingPermissions = r)"
                  v-model="user.tradingPermissions"
                  :error-messages="clErrors.tradingPermissions"
                  :item-text="formatTradingPermissionsLabel"
                  item-value="permission"
                  :items="allowedPermissions"
                  :label="$t(`tradingPermissions.title`)"
                ></VSelect>
              </VCol>
            </VRow>
            <template v-if="company !== undefined && companyKind !== 'broker'">
              <VRow v-if="user.tradingPermissions === null">
                <VCol cols="12">
                  <VSheet class="elevation-14" elevation="10">
                    <VAlert border="left" dense icon="mdi-information" type="info">
                      <span>
                        {{ $t('tradingPermissions.title') }} for the company are:
                        <em>
                          {{ $t(`tradingPermissions.options.${company.tradingPermissions}`) }}
                        </em>
                      </span>
                    </VAlert>
                  </VSheet>
                </VCol>
              </VRow>
              <VRow
                v-else-if="
                  // no need to explain 'disabled'
                  user.tradingPermissions !== 0 &&
                  // and no need to explain when the permissions are the same
                  effectiveTradingPermissions !== user.tradingPermissions
                "
              >
                <VCol cols="12">
                  <VAlert border="left" dense icon="mdi-information" type="info">
                    <div>User permissions may be limited by permissions set on the company.</div>
                    <div>
                      Trading permissions for {{ company.name }} are:
                      <em>
                        {{ $t(`tradingPermissions.options.${company.tradingPermissions}`) }}.
                      </em>
                    </div>
                    <div>
                      As a result, the effective trading permissions of this user are:
                      <em>
                        {{ $t(`tradingPermissions.options.${effectiveTradingPermissions}`) }}.
                      </em>
                    </div>
                  </VAlert>
                </VCol>
              </VRow>
            </template>

            <VRow v-if="crudAction === RestOperation.Update">
              <VCol>
                <VCheckbox
                  v-model="user.resetPassword"
                  label="Send a Reset Password Link to the email address above"
                >
                </VCheckbox>
              </VCol>
            </VRow>
          </VContainer>

          <VAlert
            v-if="originalCompany && company && originalCompany.id !== company.id"
            border="left"
            dense
            icon="mdi-alert"
            type="warning"
          >
            You're about to change the company for this user from
            {{ originalCompany.name }} to {{ company.name }}!
          </VAlert>
        </VCardText>

        <VCardActions class="pt-4">
          <VBtn class="d-none d-sm-flex" color="secondary" text @click="closeModalDialog">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer></VSpacer>
          <VBtn
            color="primary"
            :disabled="processing"
            :loading="processing"
            min-width="120"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('adminSaveUser') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped></style>
